import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useListCustomersTopLevel, useListCustomerProfiles, AcceptedCommodity, acceptedCommodityTypes, RiskCropProfile, RiskCropProfileHistory } from '../../lib/api/risk/profiles';
import { custom, z } from 'zod';
import { GeneralFiltersValidation } from '../../lib/api/risk/profiles';
import { GenerateLoadingOrError } from '@/lib/utils';
import { Combobox } from '@/components/ui/combobox';
import { BearDecimalInput, BearIntInput } from '@/lib/form/ui';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import useDebounce from '../otc/useDebounce';
import { useSearchParams } from 'react-router-dom';
import '@/styles/riskCompactTable.css';
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { DataTable } from "@/components/ui/dataTable";
import { ColumnDef } from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@radix-ui/react-collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChevronDown, ChevronRight, Search, Plus, Edit } from "lucide-react";
import { CreateCropProfileDialog, EditCropProfileDialog } from './CropProfileForm';
import { useListCustomers } from '@/lib/api/customer';
import { cn } from '@/lib/utils';
import { formatCommodityToUom, formatNumberToDisplay, safeParseDecimal } from '@/lib/api/common';
import { Separator } from '@/components/ui/separator';
import { Uom } from '@/lib/api/commodity';
import { RiskProfileDetailModal } from '@/components/modals/RiskProfileDetailModal';
import { CropProfileReportModal } from '@/section/risk/GenerateProfileReportModal';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import SummaryCard from './SummaryCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import RiskManagementSummaryTables from './SummaryTables';
import { useListTraders } from '@/lib/api/trader';
import { Trader } from '@/lib/api/reports';
import { RiskCustomerModal } from '@/components/modals/RiskCustomerModal';


// Extend the FilterState to support array of crop years
type FiltersState = Omit<z.infer<typeof GeneralFiltersValidation>, 'crop_year'> & {
  trader_customer_id?: string;
  trader_id?: string;
  crop_year?: number[];
  searchTerms?: string[];
};

// Structure for organizing profiles by customer, commodity, and crop year
interface CustomerGroup {
  customerId: string;
  customerName: string;
  clientId: string;
  profiles: RiskCropProfile[];
}

type CustomerGroupMap = Record<string, CustomerGroup>;

interface SearchFilterState {
  searchTerm: string;
  showFilters: boolean;
}

type ExpandedState = Record<string, boolean>;

export function RiskManagementDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentTab, setTab] = useState<string>(searchParams.get("tab") || 'graph_summary');
  const customerId = searchParams.get('customer_id');
  const profileId = searchParams.get('profile_id');

  useEffect(() => {
    if (searchParams.get("tab") && searchParams.get("tab") !== currentTab) {
      setTab(searchParams.get("tab")!);
    }
    if (["graph_summary", "table_summary"].includes(currentTab) && customerId) {
      setTab("customer");
    }
  }, [customerId, searchParams.get("tab") || "", currentTab]);

  const setSpecificParam = (paramName: string, paramValue: string) => {
    setSearchParams(prevParams => {
      const newParams = new URLSearchParams(prevParams);
      newParams.set(paramName, paramValue);
      return newParams;
    });
  };

  const onTabChange = (tab: string) => {
    setTab(tab);
    if (["graph_summary", "table_summary"].includes(tab) && !!customerId) {
      setSearchParams({ tab });
    } else {
      setSpecificParam("tab", tab);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold">Risk Management Dashboard</h1>
      <Tabs value={currentTab} onValueChange={(tab) => onTabChange(tab)}>
        <TabsList className="mb-4">
          <TabsTrigger value="graph_summary">Graph Summary</TabsTrigger>
          <TabsTrigger value="table_summary">Table Summary</TabsTrigger>
          <TabsTrigger value="customer">Customer Breakdown</TabsTrigger>
        </TabsList>

        <TabsContent value="graph_summary">
          <RiskCropProfileTopLevel view_type='graph_summary' tab={currentTab} />
        </TabsContent>
        <TabsContent value="table_summary">
          <RiskCropProfileTopLevel view_type='table_summary' tab={currentTab} />
        </TabsContent>
        <TabsContent value="customer">
          <RiskCropProfileCustomerProfiles selectedCustomerId={customerId!} selectedProfileId={profileId} />
        </TabsContent>
      </Tabs>
    </div>
  )
};

export default RiskManagementDashboard;

function RiskCropProfileTopLevel({ view_type, tab }: { view_type: "graph_summary" | "table_summary"; tab: string }) {
  const [searchParams, setSearchParams] = useSearchParams();
  // Initialize filters from URL parameters
  const [filters, setFilters] = useState<FiltersState>(() => {
    const initialFilters: FiltersState = { is_enabled: true };

    // Get crop years from URL (can be multiple)
    const cropYearParams = searchParams.getAll('crop_year');
    if (cropYearParams.length > 0) {
      initialFilters.crop_year = cropYearParams.map(year => parseInt(year, 10)).filter(year => !isNaN(year));
    }

    // Get commodities from URL (can be multiple)
    const commoditiesParam = searchParams.getAll('commodities');
    if (commoditiesParam.length > 0) {
      initialFilters.commodities = commoditiesParam as AcceptedCommodity[];
    }

    const traderIdParam = searchParams.get('trader_id');
    if (traderIdParam) {
      initialFilters.trader_id = traderIdParam;
    }

    const traderCustomerIdParam = searchParams.get('trader_customer_id');
    if (traderCustomerIdParam) {
      initialFilters.trader_customer_id = traderCustomerIdParam;
    }

    return initialFilters;
  });

  const [searchFilter, setSearchFilter] = useState<SearchFilterState>({
    searchTerm: '',
    showFilters: true,
  });
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [cropYearInput, setCropYearInput] = useState<string>('');
  const [searchTerms, setSearchTerms] = useState<string[]>([]);

  const debouncedFilters = useDebounce(filters, 300);
  const debouncedSearchTerm = useDebounce(searchFilter.searchTerm, 300);

  // Prepare filters for API - convert array of crop years to single value the API expects
  const prepareApiFilters = (filters: FiltersState): z.infer<typeof GeneralFiltersValidation> => {
    const apiFilters: z.infer<typeof GeneralFiltersValidation> = {
      ...filters,
      // API expects single crop_year, use the first one from our array if available
      crop_year: filters.crop_year && filters.crop_year.length > 0 ? filters.crop_year : undefined,
      trader_id: filters.trader_id,
      customer_id: filters.trader_customer_id,
    };

    return apiFilters;
  };

  const tradersResult = useListTraders();
  const baseTopLevelResult = useListCustomersTopLevel({ filters: { is_enabled: true, trader_id: debouncedFilters.trader_id, customer_id: debouncedFilters.trader_customer_id } });
  const topLevelResult = useListCustomersTopLevel({ filters: prepareApiFilters(debouncedFilters) });
  const customersRes = useListCustomers({ minimal: true });

  // Group profiles by customer, then by commodity and crop year
  const groupedProfiles = useMemo(() => {
    if (!topLevelResult.data?.success || !topLevelResult.data?.data?.profiles) {
      return {} as CustomerGroupMap;
    }

    const profiles = topLevelResult.data.data.profiles;
    const groupedByCustomer: CustomerGroupMap = {};

    profiles.forEach((profile: RiskCropProfile) => {
      if (!profile.Customer) return;

      const customerId = profile.customer_id;

      if (!groupedByCustomer[customerId]) {
        groupedByCustomer[customerId] = {
          customerId,
          customerName: profile.Customer.long_name,
          clientId: profile.Customer.client_id,
          profiles: [],
        };
      }

      groupedByCustomer[customerId].profiles.push(profile);
    });

    // Sort profiles within each customer group by commodity and then crop year
    Object.values(groupedByCustomer).forEach(customerGroup => {
      customerGroup.profiles.sort((a, b) => {
        // First sort by commodity name
        if (a.commodity_name < b.commodity_name) return -1;
        if (a.commodity_name > b.commodity_name) return 1;

        // Then sort by crop year (descending)
        return a.crop_year - b.crop_year;
      });
    });

    return groupedByCustomer;
  }, [topLevelResult.data]);

  // Filter customers by search term
  const filteredCustomers = useMemo(() => {
    const customers = Object.values(groupedProfiles);
    if (!debouncedSearchTerm) return customers;

    const searchLower = debouncedSearchTerm.toLowerCase();
    return customers.filter(
      customer =>
        customer.customerName.toLowerCase().includes(searchLower) ||
        customer.clientId.toLowerCase().includes(searchLower) ||
        customer.profiles.some(profile =>
          profile.commodity_name.toLowerCase().includes(searchLower) ||
          profile.crop_year.toString().includes(searchLower)
        )
    );
  }, [groupedProfiles, debouncedSearchTerm]);

  const { knownCropYears } = useMemo(() => {
    const knownCropYears = (baseTopLevelResult?.data?.data?.profiles || []).reduce((acc: number[], profile: RiskCropProfile) => {
      if (acc.includes(profile.crop_year)) return acc;
      acc.push(profile.crop_year);
      return acc;
    }, [] as number[]);

    return { knownCropYears: knownCropYears.sort().reverse() };
  }, [baseTopLevelResult]);

  // Customer options for combobox
  const { traderOptions, existingCustomerOptions, allCustomerOptions } = useMemo(() => {
    const customersList = customersRes?.data?.data || [];
    const tradersList = tradersResult?.data?.data || [];

    return {
      traderOptions: tradersList.map((trader: Trader) => ({
        value: trader.id,
        label: `${trader.name}`,
      })),
      existingCustomerOptions: Object.values(groupedProfiles).map(customer => ({
        value: customer.customerId,
        label: `${customer.customerName}`,
      })),
      allCustomerOptions: customersList.map((customer: { id: string; long_name: string }) => ({
        value: customer.id,
        label: `${customer.long_name}`,
      })),
    };
  }, [groupedProfiles, customersRes, tradersResult]);

  const updateSearchParams = (newFilters: FiltersState) => {
    const params = new URLSearchParams(searchParams);

    // Clear existing filter params
    params.delete('crop_year');
    params.delete('commodities');
    params.delete('trader_id');
    params.delete('trader_customer_id');
    params.delete('search');

    // Add current filters to URL params
    if (newFilters.crop_year && newFilters.crop_year.length > 0) {
      newFilters.crop_year.forEach(year => {
        params.append('crop_year', year.toString());
      });
    }

    if (newFilters.commodities && newFilters.commodities.length > 0) {
      // Clear then add all commodities
      newFilters.commodities.forEach(commodity => {
        params.append('commodities', commodity);
      });
    }

    if (newFilters.trader_id) {
      params.append("trader_id", newFilters.trader_id);
    }


    if (newFilters.trader_customer_id) {
      params.append("trader_customer_id", newFilters.trader_customer_id);
    }


    // Add search terms to URL
    if (searchTerms.length > 0) {
      searchTerms.forEach(term => {
        params.append('search', term);
      });
    }

    setSearchParams(params);
  };

  const handleFilterChange = <K extends keyof FiltersState>(key: K, value: FiltersState[K]) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      updateSearchParams(newFilters);
      return newFilters;
    });
  };

  const handleCustomerChange = (customerId: string) => {
    if (customerId === "") {
      return; // Do nothing on empty selection
    } else {
      navigateToCustomerProfile(customerId);
    }
  };

  const handleSelectTrader = (traderId: string) => {
    handleFilterChange("trader_id", traderId);
    handleFilterChange("trader_customer_id", "");
  };

  const handleSelectTraderCustomer = (customerId: string) => {
    handleFilterChange("trader_customer_id", customerId);
  };

  const handleCommodityChange = (commodityValue: string) => {
    if (commodityValue === "") {
      return; // Do nothing on empty selection
    } else {
      // Add commodity to existing array
      setFilters(prev => {
        const currentCommodities = prev.commodities || [];
        if (!currentCommodities.includes(commodityValue as AcceptedCommodity)) {
          const newFilters = {
            ...prev,
            commodities: [...currentCommodities, commodityValue as AcceptedCommodity]
          };
          updateSearchParams(newFilters);
          return newFilters;
        }
        return prev;
      });
    }
  };

  const handleCropYearChange = (cropYear: string) => {
    if (cropYear === "") {
      return; // Do nothing on empty selection
    } else {
      // Add commodity to existing array
      setFilters(prev => {
        const currentCropYears = prev.crop_year || [];
        if (!currentCropYears.includes(parseInt(cropYear))) {
          const newFilters = {
            ...prev,
            crop_year: [...currentCropYears, parseInt(cropYear)]
          };
          updateSearchParams(newFilters);
          return newFilters;
        }
        return prev;
      });
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchFilter(prev => ({ ...prev, searchTerm: e.target.value }));
  };

  // Add search term on Enter key press
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchFilter.searchTerm.trim()) {
      e.preventDefault();
      // Add to searchTerms array if not already there
      setSearchTerms(prev => {
        const updated = [...prev];
        if (!updated.includes(searchFilter.searchTerm.trim())) {
          updated.push(searchFilter.searchTerm.trim());
        }
        // Update URL with new search terms
        const params = new URLSearchParams(searchParams);
        params.delete('search');
        updated.forEach(term => {
          params.append('search', term);
        });
        setSearchParams(params);
        return updated;
      });
      // Clear the search input
      setSearchFilter(prev => ({ ...prev, searchTerm: '' }));
    }
  };

  // Remove a search term
  const removeSearchTerm = (term: string) => {
    setSearchTerms(prev => {
      const updated = prev.filter(t => t !== term);
      // Update URL params
      const params = new URLSearchParams(searchParams);
      params.delete('search');
      updated.forEach(t => {
        params.append('search', t);
      });
      setSearchParams(params);
      return updated;
    });
  };

  const handleRemoveFilter = <K extends keyof FiltersState>(key: K, value?: string | number) => {
    setFilters(prev => {
      const newFilters = { ...prev };

      if (key === 'commodities' && typeof value === 'string') {
        // Remove specific commodity
        newFilters.commodities = newFilters.commodities?.filter(c => c !== value) || [];
        if (newFilters.commodities.length === 0) {
          delete newFilters[key];
        }
      } else if (key === 'crop_year' && typeof value === 'number') {
        // Remove specific crop year
        newFilters.crop_year = newFilters.crop_year?.filter(y => y !== value) || [];
        if (newFilters.crop_year.length === 0) {
          delete newFilters[key];
        }
      } else {
        delete newFilters[key];
      }

      updateSearchParams(newFilters);
      return newFilters;
    });
  };

  const toggleExpanded = (customerId: string) => {
    setExpanded(prev => ({
      ...prev,
      [customerId]: !prev[customerId]
    }));
  };

  const handleClearFilters = () => {
    setFilters({ is_enabled: true });
    setSearchParams({ tab });
  };

  const navigateToCustomerProfile = (customerId: string, profileId?: string) => {
    const params = new URLSearchParams();
    params.set('customer_id', customerId);
    if (profileId) {
      params.set('profile_id', profileId);
    }
    setSearchParams(params);
  };

  const loadingOrErrorUi = GenerateLoadingOrError([topLevelResult, baseTopLevelResult]);
  if (loadingOrErrorUi) return loadingOrErrorUi;

  const { data: topLevelData, isLoading: isLoadingTopLevel, error: errorTopLevel } = topLevelResult;
  const summaries = topLevelData?.data?.summaries || {};

  return (
    <div className="p-4 space-y-6">
      <div className="flex flex-col lg:flex-row gap-4 lg:justify-between ">
        <div className="p-3 border rounded-md space-y-3">
          <span className="flex flex-row justify-between">
            <h2 className="text-xl font-semibold">Filters</h2>
            <span>
              <Button variant="outline" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            </span>
          </span>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="col-span-1">
              <label htmlFor="searchFilter" className="block text-sm font-medium text-gray-700">Jump To Existing Customer Profiles</label>
              <div className="relative">
                <Combobox
                  dwimOnChange
                  virtualizeList
                  options={existingCustomerOptions}
                  value=""
                  onValueChange={handleCustomerChange}
                  placeholder="Select Customer"
                  className="w-full"
                  popoverClassName='md:min-w-[300px] lg:min-w-[400px] xl:min-w-[500px]'
                />
              </div>
            </div>
            <div className="col-span-1 w-full">
              <label htmlFor="commodityFilter" className="block text-sm font-medium text-gray-700">Commodity</label>
              <Combobox
                dwimOnChange
                options={acceptedCommodityTypes.map(c => ({ value: c, label: c }))}
                value=""
                onValueChange={handleCommodityChange}
                placeholder="Select Commodity"
                className="sm:min-w-[200px] md:w-full lg:w-full"
                popoverClassName="sm:min-w-[200px] md:min-w-[300px]"
              />
              {filters.commodities && filters.commodities.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {filters.commodities.map((commodity: string) => (
                    <Badge key={commodity} variant="secondary" className="text-xs">
                      {commodity}
                      <button
                        className="ml-1 hover:bg-gray-200 rounded-full"
                        onClick={() => handleRemoveFilter('commodities', commodity)}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <div className="col-span-1">
              <label htmlFor="cropYearFilter" className="block text-sm font-medium text-gray-700">Crop Year</label>
              <div className="relative">
                <Combobox
                  id="cropYearFilter"
                  dwimOnChange
                  options={knownCropYears.map((c: number) => ({ value: c.toString(), label: c.toString() }))}
                  value=""
                  onValueChange={handleCropYearChange}
                  placeholder="Select Crop Year"
                  className="sm:min-w-[200px] md:min-w-[300px]"
                  popoverClassName="sm:min-w-[200px] md:min-w-[300px]"
                />
              </div>
              {filters.crop_year && filters.crop_year.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {filters.crop_year.map((year) => (
                    <Badge key={year} variant="secondary" className="text-xs">
                      {year}
                      <button
                        className="ml-1 hover:bg-gray-200 rounded-full"
                        onClick={() => handleRemoveFilter('crop_year', year)}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <div className="col-span-1">
              <label htmlFor="searchFilter" className="block text-sm font-medium text-gray-700">Merchandiser</label>
              <div className="relative">
                <Combobox
                  dwimOnChange
                  virtualizeList
                  options={traderOptions}
                  value={searchParams.get("trader_id") || ""}
                  onValueChange={handleSelectTrader}
                  placeholder="Select Merchandiser"
                  className="w-full"
                  popoverClassName='md:min-w-[300px] lg:min-w-[400px] xl:min-w-[500px]'
                />
              </div>
            </div>
            <div className="col-span-1">
              <label htmlFor="searchFilter" className="block text-sm font-medium text-gray-700">Merchandiser Customer</label>
              <div className="relative">
                <Combobox
                  dwimOnChange
                  virtualizeList
                  options={existingCustomerOptions}
                  value={searchParams.get("trader_customer_id") || ""}
                  onValueChange={handleSelectTraderCustomer}
                  placeholder="Select Merchandiser Customer"
                  className="w-full"
                  popoverClassName='md:min-w-[300px] lg:min-w-[400px] xl:min-w-[500px]'
                />
              </div>
            </div>
          </div>
        </div>
        <span className="flex flex-col gap-4">
          <CreateCropProfileDialog onSuccess={() => topLevelResult.refetch()} customerOptions={allCustomerOptions} />
          <CropProfileReportModal options={{
            customer_id: undefined,
            filters,
          }} />
        </span>
      </div>

      {view_type === "graph_summary" && (
        <SummaryCard data={summaries} isLoading={false} customers={customersRes?.data?.data || []} />
      )}

      {view_type === "table_summary" && (
        <div className="flex flex-col p-3 gap-4">
          <h2 className="text-xl font-semibold mb-3">Customer Crop Profiles</h2>

          <RiskManagementSummaryTables isLoading={false} data={topLevelData?.data!} />

          <ScrollArea className="h-[550px]">
            <Table className="risk-compact-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[20px] py-1"></TableHead>
                  <TableHead className="py-1">Customer</TableHead>
                  <TableHead className="py-1">Client ID</TableHead>
                  <TableHead className="py-1">Profiles</TableHead>
                  <TableHead className="py-1">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCustomers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-4">
                      No customers found with the current filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCustomers.map((customer) => (
                    <React.Fragment key={customer.customerId}>
                      <TableRow
                        className="cursor-pointer hover:bg-gray-50 customer-row"
                        onClick={() => toggleExpanded(customer.customerId)}
                      >
                        <TableCell>
                          {expanded[customer.customerId] ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </TableCell>
                        <TableCell className="font-medium text-sm">{customer.customerName}</TableCell>
                        <TableCell className="text-sm">{customer.clientId}</TableCell>
                        <TableCell className="text-sm">{customer.profiles.length}</TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs h-7 py-0 px-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigateToCustomerProfile(customer.customerId);
                            }}
                          >
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                      {expanded[customer.customerId] && (
                        <TableRow>
                          <TableCell colSpan={5} className="p-0">
                            <div className="bg-gray-50 p-2 expanded-row-content">
                              <Table className="risk-compact-table">
                                <TableHeader>
                                  <TableRow>
                                    <TableHead className="w-[200px] py-1 text-xs">Commodity</TableHead>
                                    <TableHead className="py-1 text-xs">Crop Year</TableHead>
                                    <TableHead className="py-1 text-xs">Acreage</TableHead>
                                    <TableHead className="py-1 text-xs">Projected Yield</TableHead>
                                    <TableHead className="py-1 text-xs">Actual Yield</TableHead>
                                    <TableHead className="py-1 text-xs">Actions</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {customer.profiles.map((profile) => (
                                    <TableRow key={profile.id}>
                                      <TableCell className="text-xs">{profile.commodity_name?.trim() || ""}</TableCell>
                                      <TableCell className="text-xs">{profile.crop_year || ""}</TableCell>
                                      <TableCell className="text-xs">{formatNumberToDisplay(profile.acreage, { emptyFormat: "--", zeroFormat: "--" })}</TableCell>
                                      <TableCell className="text-xs">{formatNumberToDisplay(profile.projected_yield, { emptyFormat: "--", zeroFormat: "--", suffix: formatCommodityToUom(profile.commodity_type) })}</TableCell>
                                      <TableCell className="text-xs">{formatNumberToDisplay(profile.actual_yield, { emptyFormat: "--", zeroFormat: "--", suffix: formatCommodityToUom(profile.commodity_type) })}</TableCell>
                                      <TableCell>
                                        <div className="action-buttons">
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            className="text-xs h-6 py-0 px-2"
                                            onClick={() => navigateToCustomerProfile(customer.customerId, profile.id)}
                                          >
                                            View
                                          </Button>
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </div>
      )}
    </div>
  )
}

function RiskCropProfileCustomerProfiles({ selectedCustomerId, selectedProfileId }: { selectedCustomerId?: string; selectedProfileId?: string | null }) {
  const [isEditing, setIsEditing] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  // Initialize filters from URL parameters
  const [filters, setFilters] = useState<FiltersState>(() => {
    const initialFilters: FiltersState = { is_enabled: true };

    // Get crop years from URL (can be multiple)
    const cropYearParams = searchParams.getAll('crop_year');
    if (cropYearParams.length > 0) {
      initialFilters.crop_year = cropYearParams.map(year => parseInt(year, 10)).filter(year => !isNaN(year));
    }

    // Get commodities from URL (can be multiple)
    const commoditiesParam = searchParams.getAll('commodities');
    if (commoditiesParam.length > 0) {
      initialFilters.commodities = commoditiesParam as AcceptedCommodity[];
    }

    return initialFilters;
  });

  const [searchFilter, setSearchFilter] = useState<SearchFilterState>({
    searchTerm: '',
    showFilters: true,
  });
  const [cropYearInput, setCropYearInput] = useState<string>('');
  const [searchTerms, setSearchTerms] = useState<string[]>(() => {
    // Initialize from URL search parameters if available
    return searchParams.getAll('search');
  });
  const isShowingActivities = searchParams.get("showActivity") === "true";
  const debouncedFilters = useDebounce(filters, 300);

  useEffect(() => {
    if (isEditing && !searchParams.get("profile_id")) {
      setIsEditing(false);
    }
  }, [isEditing, searchParams.get("profile_id")])

  const closeDetailModalAndClearUrl = useCallback(() => {
    if (searchParams.get('profile_id')) {
      const params = new URLSearchParams(searchParams);
      params.delete('profile_id');
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Prepare filters for API - convert array of crop years to single value the API expects
  const prepareApiFilters = (filters: FiltersState): z.infer<typeof GeneralFiltersValidation> => {
    const apiFilters: z.infer<typeof GeneralFiltersValidation> = {
      ...filters,
      // API expects single crop_year, use the first one from our array if available
      crop_year: filters.crop_year && filters.crop_year.length > 0 ? filters.crop_year : undefined
    };
    return apiFilters;
  };

  const baseTopLevelResult = useListCustomersTopLevel({ filters: { is_enabled: true } });

  const baseCustomerProfileResult = useListCustomerProfiles({
    customer_id: selectedCustomerId,
    filters: { is_enabled: true }
  });

  const customerProfilesResult = useListCustomerProfiles({
    customer_id: selectedCustomerId,
    filters: prepareApiFilters(debouncedFilters)
  });

  const updateSearchParams = (newFilters: FiltersState) => {
    const params = new URLSearchParams(searchParams);

    // Clear existing filter params
    params.delete('crop_year');
    params.delete('commodities');

    // Add current filters to URL params
    if (newFilters.crop_year) {
      params.set('crop_year', newFilters.crop_year.toString());
    }

    if (newFilters.commodities && newFilters.commodities.length > 0) {
      // Clear then add all commodities
      newFilters.commodities.forEach(commodity => {
        params.append('commodities', commodity);
      });
    }

    setSearchParams(params);
  };

  const handleFilterChange = <K extends keyof FiltersState>(key: K, value: FiltersState[K]) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      updateSearchParams(newFilters);
      return newFilters;
    });
  };

  const handleCommodityChange = (commodityValue: string) => {
    if (commodityValue === "") {
      return; // Do nothing on empty selection
    } else {
      // Add commodity to existing array
      setFilters(prev => {
        const currentCommodities = prev.commodities || [];
        if (!currentCommodities.includes(commodityValue as AcceptedCommodity)) {
          const newFilters = {
            ...prev,
            commodities: [...currentCommodities, commodityValue as AcceptedCommodity]
          };
          updateSearchParams(newFilters);
          return newFilters;
        }
        return prev;
      });
    }
  };

  const handleCustomerChange = (customer_id: string) => {
    const params = new URLSearchParams(searchParams);
    if (params.get('profile_id')) {
      params.delete('profile_id');
    }
    params.set('customer_id', customer_id);
    setSearchParams(params, { replace: true });
    return;
  };

  const handleCropYearChange = (cropYear: string) => {
    if (cropYear === "") {
      return; // Do nothing on empty selection
    } else {
      // Add commodity to existing array
      setFilters(prev => {
        const currentCropYears = prev.crop_year || [];
        if (!currentCropYears.includes(parseInt(cropYear))) {
          const newFilters = {
            ...prev,
            crop_year: [...currentCropYears, parseInt(cropYear)]
          };
          updateSearchParams(newFilters);
          return newFilters;
        }
        return prev;
      });
    }
  };

  const handleRemoveFilter = <K extends keyof FiltersState>(key: K, value?: string) => {
    setFilters(prev => {
      const newFilters = { ...prev };

      if (key === 'commodities' && value) {
        // Remove specific commodity
        newFilters.commodities = newFilters.commodities?.filter(c => c !== value) || [];
        if (newFilters.commodities.length === 0) {
          delete newFilters[key];
        }
      } else if (key === 'crop_year') {
        newFilters.crop_year = newFilters.crop_year?.filter(c => c !== parseInt(value || "0")) || [];
        if (newFilters.crop_year.length === 0) {
          delete newFilters[key];
        }
      } else {
        delete newFilters[key];
      }

      updateSearchParams(newFilters);
      return newFilters;
    });
  };

  const handleClearFilters = () => {
    setFilters({ is_enabled: true });
    setSearchParams({ customer_id: selectedCustomerId || "" });
  };

  const handleBackToList = () => {
    setSearchParams({});
  };

  const onCloseEditDialog = () => {
    setSearchParams({ customer_id: selectedCustomerId || "" });
    setIsEditing(false);
  };

  const { data: customerProfileData, isLoading: isLoadingCustomerProfiles, error: errorCustomerProfiles } = customerProfilesResult;
  const profiles = customerProfileData?.data?.profiles || [];
  const histories = customerProfileData?.data?.histories || [];
  const summaries = customerProfileData?.data?.summaries || {};
  const customerName = profiles.length > 0 ? profiles[0].Customer.long_name : "Customer";
  const clientId = profiles.length > 0 ? profiles[0].Customer.client_id : "";

  const { isDetailViewOpen, selectedProfile, selectedProfileForDetail, selectedProfileHistoriesForDetail } = useMemo(() => {
    const selectedProfile = selectedProfileId ? profiles.find((p: RiskCropProfile) => p.id === selectedProfileId) : undefined;
    const isDetailViewOpen = !isEditing && !!selectedProfile;
    return {
      isDetailViewOpen,
      selectedProfile,
      selectedProfileForDetail: selectedProfile,
      selectedProfileHistoriesForDetail: histories.filter((history: RiskCropProfileHistory) => history.profile_id === selectedProfile?.id)
    };
  }, [profiles, histories, isEditing, selectedProfileId]);

  const editingProfile = isEditing && selectedProfile ? selectedProfile : undefined;

  const { knownCustomerOptions, knownCommodities, knownCropYears } = useMemo(() => {
    const knownCustomersMap = (baseTopLevelResult?.data?.data?.profiles || []).reduce((acc: Record<string, any>, profile: RiskCropProfile) => {
      if (!!acc[profile.customer_id]) return acc;
      acc[profile.customer_id] = profile.Customer;
      return acc;
    }, {} as Record<string, any>);
    const knownCustomerOptions = Object.values(knownCustomersMap).map((customer: any) => ({ value: customer.id, label: customer.long_name })).sort((a, b) => a.label.localeCompare(b.label));

    const knownCommodities = (baseCustomerProfileResult?.data?.data?.profiles || []).reduce((acc: string[], profile: RiskCropProfile) => {
      if (acc.includes(profile.commodity_name)) return acc;
      acc.push(profile.commodity_name);
      return acc;
    }, [] as string[]);

    const knownCropYears = (baseCustomerProfileResult?.data?.data?.profiles || []).reduce((acc: number[], profile: RiskCropProfile) => {
      if (acc.includes(profile.crop_year)) return acc;
      acc.push(profile.crop_year);
      return acc;
    }, [] as number[]);



    return { knownCustomerOptions, knownCommodities, knownCropYears: knownCropYears.sort().reverse() };
  }, [
    baseTopLevelResult.data?.data,
    baseCustomerProfileResult.data?.data,
  ]);

  const loadingOrErrorUi = GenerateLoadingOrError([...(selectedCustomerId ? [customerProfilesResult, baseCustomerProfileResult] : []), baseTopLevelResult]);
  if (loadingOrErrorUi) return loadingOrErrorUi;

  // Define columns for the table view
  const columns: ColumnDef<RiskCropProfile, any>[] = [
    {
      accessorKey: "commodity_name",
      header: "Commodity",
      onCellClick: true,
    },
    {
      accessorKey: "crop_year",
      header: "Crop Year",
      onCellClick: true,
    },
    {
      accessorKey: "acreage",
      header: "Acreage",
      onCellClick: true,
      cell: ({ row }) => formatNumberToDisplay(row.original.acreage, { emptyFormat: "--", zeroFormat: "--" }),
    },
    {
      accessorKey: "projected_yield",
      header: "Projected Yield",
      onCellClick: true,
      cell: ({ row }) => formatNumberToDisplay(row.original.projected_yield, { emptyFormat: "--", zeroFormat: "--" }),
    },
    {
      accessorKey: "actual_yield",
      header: "Actual Yield",
      onCellClick: true,
      cell: ({ row }) => formatNumberToDisplay(row.original.actual_yield, { emptyFormat: "--", zeroFormat: "--" }),
    },
    {
      accessorKey: "projected_projected",
      header: "Projected Production",
      onCellClick: true,
      cell: ({ row }) => formatNumberToDisplay(row.original.projected_total_production, { emptyFormat: "--", zeroFormat: "--", suffix: formatCommodityToUom(row.original.commodity_type) }),
    },
    {
      accessorKey: "actual_production",
      header: "Actual Production",
      onCellClick: true,
      cell: ({ row }) => formatNumberToDisplay(row.original.actual_total_production, { emptyFormat: "--", zeroFormat: "--", suffix: formatCommodityToUom(row.original.commodity_type) }),
    },
    {
      id: "actions",
      isCellClick: false,
      cell: ({ row }: any) => (
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const params = new URLSearchParams(searchParams);
            params.set('profile_id', row.original.id);
            setSearchParams(params);
            setIsEditing(true);
          }}
        >
          Edit
        </Button>
      ),
    } as any,
  ];

  return (
    <div className="p-4 space-y-6">
      <div className="flex justify-between items-center">
        <Button variant="outline" onClick={handleBackToList}>
          Back to List
        </Button>
        <h1 className="text-2xl font-bold">
          {customerName}
        </h1>
        <div className="flex gap-2">
          <CreateCropProfileDialog
            customerId={selectedCustomerId}
            onSuccess={() => customerProfilesResult.refetch()}
            customerOptions={[]}
          />
          <CropProfileReportModal options={{
            customer_id: selectedCustomerId,
            filters: prepareApiFilters(debouncedFilters),
          }} />
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <div className="p-3 border rounded-md space-y-3">
          <span className="flex flex-row justify-between">
            <h2 className="text-xl font-semibold">Filters</h2>
            <span>
              <Button variant="outline" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            </span>
          </span>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="col-span-1 w-full">
              <label htmlFor="commodityFilter" className="block text-sm font-medium text-gray-700">Jump to Customer</label>
              <Combobox
                autofocus
                dwimOnChange
                options={knownCustomerOptions}
                value=""
                onValueChange={handleCustomerChange}
                placeholder=""
                className="sm:min-w-[200px] md:min-w-[300px]"
                popoverClassName="sm:min-w-[200px] md:min-w-[300px]"
              />
            </div>
            <div className="col-span-1 w-full">
              <label htmlFor="commodityFilter" className="block text-sm font-medium text-gray-700">Existing Commodity</label>
              <Combobox
                dwimOnChange
                options={knownCommodities.map((c: string) => ({ value: c, label: c }))}
                value=""
                onValueChange={handleCommodityChange}
                placeholder="Select Commodity"
                className="sm:min-w-[200px] md:min-w-[300px]"
                popoverClassName="sm:min-w-[200px] md:min-w-[300px]"
              />
              {filters.commodities && filters.commodities.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {filters.commodities.map((commodity) => (
                    <Badge key={commodity} variant="secondary" className="text-xs">
                      {commodity}
                      <button
                        className="ml-1 hover:bg-gray-200 rounded-full"
                        onClick={() => handleRemoveFilter('commodities', commodity)}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <div className="col-span-1">
              <label htmlFor="cropYearFilter" className="block text-sm font-medium text-gray-700">Crop Year</label>
              <div className="relative">
                <Combobox
                  id="cropYearFilter"
                  dwimOnChange
                  options={knownCropYears.map((c: number) => ({ value: c.toString(), label: c.toString() }))}
                  value=""
                  onValueChange={handleCropYearChange}
                  placeholder="Select Crop Year"
                  className="sm:min-w-[200px] md:min-w-[300px]"
                  popoverClassName="sm:min-w-[200px] md:min-w-[300px]"
                />
              </div>
              {Array.isArray(filters.crop_year) && filters.crop_year.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {filters.crop_year.map((year) => (
                    <Badge key={year} variant="secondary" className="text-xs">
                      {year}
                      <button
                        className="ml-1 hover:bg-gray-200 rounded-full"
                        onClick={() => handleRemoveFilter('crop_year', year.toString())}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {!!selectedCustomerId && (
        <div className="p-4">
          <h2 className="text-xl font-semibold mb-4">Customer Crop Profiles</h2>
          {profiles.length > 0 ? (
            <>
              <div className="block lg:hidden grid grid-cols-1 gap-4">
                {profiles.map((profile: RiskCropProfile) => (
                  <Card key={profile.id} className={`${profile.is_enabled ? '' : 'bg-gray-100 opacity-75'} ${profile.id === selectedProfileId ? 'border-2 border-blue-500' : ''}`}>
                    <CardHeader className="py-2 px-3">
                      <CardTitle>{profile.commodity_name} - {profile.crop_year}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-1 py-1 px-3">
                      <p className="flex gap-2"><strong>Acreage:</strong>{formatNumberToDisplay(profile.acreage, { emptyFormat: "--", suffix: formatCommodityToUom(profile.commodity_type) })}</p>
                      <p className="flex gap-2"><strong>Projected Yield:</strong>{formatNumberToDisplay(profile.projected_yield, { emptyFormat: "--", suffix: formatCommodityToUom(profile.commodity_type) })}</p>
                      <p className="flex gap-2"><strong>Actual Yield:</strong>{formatNumberToDisplay(profile.actual_yield, { emptyFormat: "--", suffix: formatCommodityToUom(profile.commodity_type) })}</p>
                      <div className="pt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => {
                            const params = new URLSearchParams(searchParams);
                            params.set('profile_id', profile.id);
                            setIsEditing(true);
                            setSearchParams(params);
                          }}
                        >
                          Edit
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <DataTable
                columns={columns}
                data={profiles}
                onCellClick={(row) => {
                  setIsEditing(false); // Ensure not in editing mode for detail view
                  const params = new URLSearchParams(searchParams);
                  params.set('profile_id', row.id);
                  setSearchParams(params);
                }}
                className={`w-full hidden lg:block ${selectedProfileId ? 'mb-6' : ''}`}
              />
            </>
          ) : (
            <p>No profiles found for this customer with the current filters.</p>
          )}

          {isEditing && (
            <EditCropProfileDialog
              onClose={() => {
                onCloseEditDialog();
              }}
              profile={editingProfile}
              onSuccess={() => customerProfilesResult.refetch()}
              customerOptions={[]}
            />

          )}

          <RiskProfileDetailModal
            isOpen={isDetailViewOpen}
            onClose={closeDetailModalAndClearUrl}
            profile={selectedProfileForDetail}
            histories={selectedProfileHistoriesForDetail}
          />

          {(isShowingActivities && !!selectedCustomerId) && (
            <RiskCustomerModal
              isOpen={!!selectedCustomerId}
              onClose={() => handleBackToList()}
              customerId={selectedCustomerId!}
              customerName={customerName!}
            />
          )}
        </div>
      )}
      {!selectedCustomerId && (
        <div></div>
      )}

    </div>
  );
}
